import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { getObjectByCode } from '@/threejs/objectManager';

export function useKeyframes() {
  const cruxModuleStore = useCruxModuleStore();
  const selectionStore = useSelectionStore();
  const timelinePlayerStore = useTimelinePlayerStore();

  const isKeyFramed = (key) => {
    if (!key || !selectionStore.selectedObjectId) return false;
    return cruxModuleStore.hasKeyframe(selectionStore.selectedObjectId, key);
  };

  const isAtKeyframeTime = (key) => {
    if (!key || !selectionStore.selectedObjectId) return false;
    const currentTime = timelinePlayerStore.currentTime;
    const keyframe = cruxModuleStore.getKeyframeAtCurrentTime(selectionStore.selectedObjectId, key);
    return keyframe !== undefined;
  };

  const getKeyframeValue = (key) => {
    if (!key || !selectionStore.selectedObjectId) return undefined;

    const currentTime = timelinePlayerStore.currentTime;
    const keyframe = cruxModuleStore.getKeyframeAtCurrentTime(selectionStore.selectedObjectId, key);

    // If there's a keyframe at the exact current time, return its value
    if (keyframe !== undefined) {
      return keyframe.value;
    }

    // Otherwise, calculate the interpolated value between keyframes
    return getValueBetweenKeyframes(key, currentTime);
  };

  const getValueBetweenKeyframes = (key, currentTime, objectCode = selectionStore.selectedObjectId) => {
    if (!key || !objectCode) return undefined;

    // Find the timeline layer for this object and property
    const layer = cruxModuleStore.cruxModules.find(
      (module) =>
        module.type === 'timeline_layer' && module.data.objectCode === objectCode && module.data.property === key
    );

    if (!layer || !layer.data.keyframes || layer.data.keyframes.length === 0) {
      return undefined;
    }

    const keyframes = layer.data.keyframes;

    // Find the keyframes surrounding the current time
    let previousKeyframe = null;
    let nextKeyframe = null;

    for (let i = 0; i < keyframes.length; i++) {
      if (keyframes[i].time <= currentTime) {
        previousKeyframe = keyframes[i];
      }
      if (keyframes[i].time > currentTime && !nextKeyframe) {
        nextKeyframe = keyframes[i];
        break;
      }
    }

    // Interpolate between two keyframes
    if (previousKeyframe && nextKeyframe) {
      const timeDiff = nextKeyframe.time - previousKeyframe.time;
      const valueDiff = nextKeyframe.value - previousKeyframe.value;
      const timeIntoSegment = currentTime - previousKeyframe.time;
      const t = timeIntoSegment / timeDiff;
      const interpolatedValue = valueDiff * t;

      return Number(previousKeyframe.value) + interpolatedValue;
    } else if (previousKeyframe) {
      return previousKeyframe.value;
    } else if (nextKeyframe) {
      return nextKeyframe.value;
    } else {
      return undefined;
    }
  };

  const getAllKeyframeValues = (currentTime) => {
    // Find the timeline layer for this object and property
    const layers = cruxModuleStore.cruxModules.filter((module) => module.type === 'timeline_layer');

    const objects = [];

    layers.forEach((layer) => {
      if (!objects[layer.data.objectCode]) {
        objects[layer.data.objectCode] = {};
      }

      // const object = getObjectByCode(layer.data.objectCode)

      const value = getValueBetweenKeyframes(layer.data.property, currentTime, layer.data.objectCode);
      objects[layer.data.objectCode][layer.data.property] = value;
    });

    Object.entries(objects).forEach(([objectCode, objData]) => {
      const object = getObjectByCode(objectCode);
      if (object) {
        object.applyProperties(objData);
      }
    });
  };

  return {
    isKeyFramed,
    isAtKeyframeTime,
    getKeyframeValue,
    getAllKeyframeValues,
  };
}
